import React, { useState, useEffect, useRef } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { Link } from "react-router-dom";
import moment from "moment";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import { useNavigate } from "react-router-dom";
import "../Package/Package.css"; // Reusing Package CSS as requested
import PackageTabs from "../AllTabs/PackageTabs"; // Assuming you want the same tabs
// import ConfirmationModal from "../../Common/Layout/ConfirmationModal"; // Uncomment if you have clone logic

const HolidayList = () => {
  const { user, loading } = useAuth();
  const [lists, setLists] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Modal States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // const [showCopyModal, setShowCopyModal] = useState(false); // Uncomment for Copy features
  // const [itemToCopy, setItemToCopy] = useState(null);
  // const [copyingId, setCopyingId] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  const navigate = useNavigate();
  const isInitialMount = useRef(true);

  // --- 1. FETCH DATA ---
  const GetData = async (pageNum, currentSearchTerm) => {
    setIsLoading(true);
    try {
      // USING THE API ENDPOINT STRUCTURE YOU PROVIDED
      let url = `${config.GETHOLIDAYSBYCOMPANYID}/${user?.cid}?PageNumber=${pageNum}&PageSize=${pageSize}`;

      if (currentSearchTerm) {
        url += `&search=${encodeURIComponent(currentSearchTerm)}`;
      }
      
      // Optional: If you want to filter by type specifically
      // url += `&type=Flight`; 

      const res = await apiService.getAll(url);
      
      // Assuming your pagination response structure is the same
      setLists(res.data);
      setPageNumber(res.pageNumber);
      setPageSize(res.pageSize);
      setTotalPages(res.totalPages);
      setTotalRecords(res.totalRecords);
    } catch (error) {
      console.error("Error fetching holidays:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- 2. AUTH CHECK ---
  useEffect(() => {
    if (!loading && !user) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  // --- 3. DATA LOADING LOGIC ---
  useEffect(() => {
    if (!user) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      GetData(1, ""); 
      return;
    }

    const debounceTimer = setTimeout(() => {
      const pageToFetch = searchTerm !== debouncedSearchTerm.current ? 1 : pageNumber;
      GetData(pageToFetch, searchTerm);
    }, 350);

    debouncedSearchTerm.current = searchTerm;

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [user, pageNumber, searchTerm]);

  const debouncedSearchTerm = useRef(searchTerm);

  if (loading) return <p>Loading...</p>;

  // --- 4. DELETE LOGIC ---
  const handleOpenDeleteModal = (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      try {
        // Ensure you have DELETEHOLIDAY in your config
        await apiService.remove(config.DELETEHOLIDAY, itemToDelete);
        GetData(pageNumber, searchTerm);
      } catch (error) {
        console.error("Error deleting holiday:", error);
      }
      handleCloseDeleteModal();
    }
  };

  // --- 5. PAGINATION HELPERS ---
  const GetLastPageData = () => GetData(totalPages, searchTerm);
  const GetFirstPageData = () => GetData(1, searchTerm);
  const GetPageData = (number) => {
    if (pageNumber !== number) {
      setPageNumber(number);
    }
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const renderPageNumbers = pageNumbers.map((number) => (
    <li
      className={`page-item ${pageNumber === number ? "active" : ""}`}
      key={number}
      onClick={() => GetPageData(number)}
    >
      <Link className="page-link">{number}</Link>
    </li>
  ));

  return (
    <div id="layout-wrapper">
      <Header User={user} activeSection="packages" />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                {/* Reusing PackageTabs or Create a HolidayTabs component */}
                <PackageTabs pageName="AI PACKAGES" />
              </div>
              <div className="card-body">
                {/* Search and Filter Section */}
                <div className="row g-3 mb-4">
                  <div className="col-xl-4 col-lg-6">
                    <div className="search-box">
                      <input
                        type="text"
                        className="form-control search"
                        placeholder="Search holidays..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-xl-2 col-lg-3">
                    <div className="hstack gap-2">
                      <button
                        className="btn btn-danger"
                        onClick={() => {
                          setSearchTerm("");
                          setPageNumber(1);
                        }}
                      >
                        <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                        Reset
                      </button>
                    </div>
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-grow text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading holidays...</p>
                  </div>
                ) : (
                  <>
                    <div className="row">
                      {lists.map((holidayItem) => (
                        <div
                          key={holidayItem.holidayId}
                          className="col-xl-3 col-md-6"
                        >
                          <div className="card package-card">
                            <div className="card-body">
                              <div className="package-img-container">
                                <img
                                  src={
                                    holidayItem.imageSrc ||
                                    "https://via.placeholder.com/300x200?text=Holiday"
                                  }
                                  className="img-fluid rounded"
                                  alt={holidayItem.holidayName}
                                  style={{ height: '200px', width: '100%', objectFit: 'cover' }}
                                />
                                <div className="package-price-badge">
                                  {holidayItem.currency || user?.currency || "INR"} {holidayItem.salePrice?.toLocaleString()}
                                </div>
                                <div className="package-duration-badge">
                                  {holidayItem.duration}
                                </div>
                                <span className={`package-status-badge ${holidayItem.status === 'Active' ? 'active' : 'inactive'}`}>
                                  {holidayItem.status || 'Draft'}
                                </span>
                              </div>

                              <div className="mt-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <h5 className="mb-0 text-truncate" title={holidayItem.holidayName}>
                                    {holidayItem.holidayName}
                                  </h5>
                                  {/* Holiday Table doesn't have isWebsite, removed toggle */}
                                </div>

                                <p className="text-muted mb-2 text-truncate" title={holidayItem.destinationsCover}>
                                  <i className="ri-map-pin-line align-bottom me-1"></i>
                                  {holidayItem.destinationsCover || "No Destination"}
                                </p>
                                
                                <div className="d-flex justify-content-between mb-2">
                                  <span>
                                    <i className="ri-calendar-line align-bottom me-1"></i>
                                    {holidayItem.startedDate ? moment(holidayItem.startedDate).format("DD MMM YYYY") : "N/A"}
                                  </span>
                                  <span>
                                    <i className="ri-user-line align-bottom me-1"></i>
                                    {holidayItem.noOfPersons}
                                  </span>
                                </div>

                                <div className="d-flex flex-wrap gap-1">
                                  {/* VIEW DETAILS BUTTON - Navigates to Tab 9 */}
                                  <Link
                                    to={`/holiday/${holidayItem.holidayId}`}
                                    state={{ activeTab: 9 }}
                                    className="btn btn-sm btn-warning text-black"
                                  >
                                    <i className="ri-eye-line align-bottom me-1"></i>{" "}
                                    View
                                  </Link>
                                  
                                  {/* EDIT BUTTON - Navigates to Form (Tab 0) */}
                                  <Link
                                    to={`/holiday/${holidayItem.holidayId}`}
                                    className="btn btn-sm btn-primary"
                                  >
                                    <i className="ri-pencil-line align-bottom me-1"></i>{" "}
                                    Edit
                                  </Link>
                                  
                                  {/* DELETE BUTTON */}
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleOpenDeleteModal(holidayItem.holidayId)}
                                  >
                                    <i className="ri-delete-bin-line align-bottom me-1"></i>{" "}
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {lists.length === 0 && (
                      <div className="text-center py-5">
                        <div className="text-muted">
                          <i className="ri-suitcase-line display-5"></i>
                          <h5 className="mt-3">No holidays found</h5>
                          <p>Try adjusting your search or add a new holiday.</p>
                        </div>
                      </div>
                    )}

                    <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                      <div className="flex-shrink-0">
                        <div className="text-muted">
                          Showing{" "}
                          <span className="fw-semibold">{lists.length}</span> of{" "}
                          <span className="fw-semibold">{totalRecords}</span>{" "}
                          Results
                        </div>
                      </div>
                      <ul className="pagination pagination-separated pagination-sm mb-0">
                        <li className={`page-item ${pageNumber === 1 ? "disabled" : ""}`} onClick={GetFirstPageData}>
                          <Link className="page-link">Previous</Link>
                        </li>
                        {renderPageNumbers}
                        <li className={`page-item ${pageNumber === totalPages ? "disabled" : ""}`} onClick={GetLastPageData}>
                          <Link className="page-link">Next</Link>
                        </li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <DeleteAlert
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleDelete={handleDelete}
      />
    </div>
  );
};

export default HolidayList;